import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { ConsentTriValue } from '../components/ConsentTriYesNo/ConsentTriYesNo';

type ConsentDraft = Record<number, ConsentTriValue>;

interface ConsentDraftContextValue {
  draft: ConsentDraft;
  setDraft: React.Dispatch<React.SetStateAction<ConsentDraft>>;
  setChoice: (definitionItemId: number, value: ConsentTriValue) => void;
  resetDraft: () => void;
}

const ConsentDraftContext = createContext<ConsentDraftContextValue | null>(null);

export const ConsentDraftProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [draft, setDraft] = useState<ConsentDraft>({});

  const resetDraft = useCallback(() => {
    setDraft({});
  }, []);

  const setChoice = useCallback((definitionItemId: number, value: ConsentTriValue) => {
    setDraft((prev) => ({ ...prev, [definitionItemId]: value }));
  }, []);

  const value = useMemo(
    () => ({ draft, setDraft, setChoice, resetDraft }),
    [draft, setChoice, resetDraft]
  );

  return <ConsentDraftContext.Provider value={value}>{children}</ConsentDraftContext.Provider>;
};

export function useConsentDraft(): ConsentDraftContextValue {
  const ctx = useContext(ConsentDraftContext);
  if (!ctx) {
    throw new Error('useConsentDraft must be used within ConsentDraftProvider');
  }
  return ctx;
}
